// Legacy Notification Context Adapter - Backward compatibility for old context pattern
// ✅ NO MAGIC STRINGS: Use typed enums and constants

import React, { createContext, useContext, ReactNode } from "react";

import {
  LegacyNotificationAdapter,
  useLegacyNotification,
} from "../../platform/notifications";

// Legacy notification interface for backward compatibility
export interface LegacyNotificationState {
  message: string;
  severity: "success" | "error" | "warning" | "info";
  open: boolean;
  duration?: number;
  timeoutId?: NodeJS.Timeout;
}

export interface LegacyNotificationContextType {
  notification: LegacyNotificationState | null;
  setSnackBarNotification: (
    notification: Partial<LegacyNotificationState>,
  ) => void;
  showNotification: (
    message: string,
    severity?: "success" | "error" | "warning" | "info",
  ) => void;
  clearNotification: () => void;
}

// Create the legacy context
const NotificationContext = createContext<LegacyNotificationContextType>({
  notification: null,
  setSnackBarNotification: () => {},
  showNotification: () => {},
  clearNotification: () => {},
});

// Legacy notification provider component
export const NotificationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  return (
    <LegacyNotificationAdapter>
      <NotificationContextConsumer>{children}</NotificationContextConsumer>
    </LegacyNotificationAdapter>
  );
};

// Context consumer component that bridges the gap
const NotificationContextConsumer: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const legacyNotification = useLegacyNotification();

  return (
    <NotificationContext.Provider value={legacyNotification}>
      {children}
    </NotificationContext.Provider>
  );
};

// Legacy hook for backward compatibility
export const useNotification = (): LegacyNotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider",
    );
  }
  return context;
};

// Export the context for direct use if needed
export { NotificationContext };
